import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import moment from 'moment'
import {
  FaArrowCircleLeft,
  FaArrowCircleRight,
  FaFileExcel,
  FaPrint,
} from 'react-icons/fa'
import XLSX from 'xlsx-js-style'
import PageHeader from '../../shared/PageHeader'
import CustomButton from '../../shared/CustomButton'
import DropdownField from '../../shared/DropdownField'
import MultiSelectField from '../../shared/MultiSelectField'
import useFetchData from '../../hooks/UseFetchData'
import usePersistedFilters from '../../hooks/usePersistedFilters'
import { fetchData } from '../../helpers/fetchData'
import { handlePrintData } from '../../utils/PrintData'
import { practionerDepartmentOptions } from '../../constants/UserConstants'
import { daysOptions } from '../../constants/AppointmentConstants'

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Available', value: 'Available' },
  { label: 'UnAvailable', value: 'UnAvailable' },
]

const normalizeId = value => value?.id || value?._id || ''

const defaultFilters = {
  department_ids: [],
  practitioner_ids: [],
  days: [],
  status: '',
  include_unavailable: false,
}

const exportDoctorScheduleToExcel = (
  rows,
  t,
  businessInfo,
  selectedDepartmentNames,
  selectedPractitionerNames,
  status
) => {
  const headers = [
    t('Department'),
    t('Doctor'),
    t('Speciality'),
    t('Room No'),
    t('Day'),
    t('Shift'),
    t('Start Time'),
    t('End Time'),
    t('Interval'),
    t('Limit'),
    t('Status'),
  ]
  const reportRows = rows.map(row => [
    row.department_name || '-',
    row.doctor_name || '-',
    row.speciality || '-',
    row.room_no || '-',
    row.day || '-',
    row.shift_title || '-',
    row.start_time || '-',
    row.end_time || '-',
    row.interval || '-',
    row.limit || '-',
    row.schedule_status || row.shift_status || '-',
  ])
  const sheetData = [
    [businessInfo?.name_en || t('Hospital Name')],
    [businessInfo?.name_ar || ''],
    [businessInfo?.address || ''],
    [t('Doctor Schedule Report')],
    [`${t('Printed On')}: ${moment().format('DD/MM/YYYY HH:mm')}`],
    [`${t('Department')}: ${selectedDepartmentNames}`],
    [`${t('Doctor')}: ${selectedPractitionerNames}`],
    [`${t('Status')}: ${status ? t(status) : t('All')}`],
    [],
    headers,
    ...reportRows,
  ]

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData)

  const range = XLSX.utils.decode_range(worksheet['!ref'])
  const border = {
    top: { style: 'thin', color: { rgb: 'E2E8F0' } },
    bottom: { style: 'thin', color: { rgb: 'E2E8F0' } },
    left: { style: 'thin', color: { rgb: 'E2E8F0' } },
    right: { style: 'thin', color: { rgb: 'E2E8F0' } },
  }
  const headerStyle = {
    fill: { fgColor: { rgb: '334155' } },
    font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 10, name: 'Calibri' },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border,
  }
  const dataRowStyle = {
    fill: { fgColor: { rgb: 'FFFFFF' } },
    font: { sz: 9, name: 'Calibri' },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border,
  }
  const centerCellStyle = {
    ...dataRowStyle,
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  }
  const departmentCellStyle = {
    ...dataRowStyle,
    fill: { fgColor: { rgb: 'F8FAFC' } },
    font: { bold: true, color: { rgb: '475569' }, sz: 9, name: 'Calibri' },
  }

  const titleStyle = {
    font: { bold: true, sz: 14, name: 'Calibri', color: { rgb: '111827' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  }
  const subtitleStyle = {
    font: { bold: true, sz: 11, name: 'Calibri', color: { rgb: '374151' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  }
  const metaStyle = {
    font: { sz: 9, name: 'Calibri', color: { rgb: '475569' } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
  }
  const tableHeaderRow = 9

  for (let R = 0; R <= 3; R++) {
    const cellAddress = XLSX.utils.encode_cell({ r: R, c: 0 })
    if (worksheet[cellAddress]) worksheet[cellAddress].s = R === 0 ? titleStyle : subtitleStyle
  }
  for (let R = 4; R <= 7; R++) {
    const cellAddress = XLSX.utils.encode_cell({ r: R, c: 0 })
    if (worksheet[cellAddress]) worksheet[cellAddress].s = metaStyle
  }

  for (let C = range.s.c; C <= range.e.c; ++C) {
    const headerAddress = XLSX.utils.encode_cell({ r: tableHeaderRow, c: C })
    if (worksheet[headerAddress]) worksheet[headerAddress].s = headerStyle
  }

  for (let R = tableHeaderRow + 1; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
      if (!worksheet[cellAddress]) continue
      if (C === 0) {
        worksheet[cellAddress].s = departmentCellStyle
      } else if (C >= 3) {
        worksheet[cellAddress].s = centerCellStyle
      } else {
        worksheet[cellAddress].s = dataRowStyle
      }
    }
  }

  worksheet['!cols'] = [
    { wch: 24 },
    { wch: 24 },
    { wch: 22 },
    { wch: 12 },
    { wch: 14 },
    { wch: 18 },
    { wch: 14 },
    { wch: 14 },
    { wch: 12 },
    { wch: 10 },
    { wch: 16 },
  ]
  worksheet['!rows'] = new Array(sheetData.length).fill({ hpt: 22 })
  worksheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 10 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 10 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 10 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: 10 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: 10 } },
    { s: { r: 5, c: 0 }, e: { r: 5, c: 10 } },
    { s: { r: 6, c: 0 }, e: { r: 6, c: 10 } },
    { s: { r: 7, c: 0 }, e: { r: 7, c: 10 } },
  ]

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, t('Doctor Schedule'))
  XLSX.writeFile(
    workbook,
    `doctor-schedule-report-${moment().format('YYYY-MM-DD')}.xlsx`
  )
}

const DoctorScheduleReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const printRef = useRef()
  const auth = useSelector(state => state.auth)
  const buisnessInfo = auth?.buisnessInfo
  const { data, executeFetch, loading } = useFetchData()

  const [departments, setDepartments] = useState([])
  const [practitioners, setPractitioners] = useState([])
  const [departmentsLoading, setDepartmentsLoading] = useState(false)
  const [practitionersLoading, setPractitionersLoading] = useState(false)

  const { filters, setFilters } = usePersistedFilters(
    'doctor_schedule_report',
    defaultFilters
  )

  useEffect(() => {
    fetchDepartments()
    fetchPractitioners()
  }, [])

  const fetchDepartments = async () => {
    setDepartmentsLoading(true)
    const response = await fetchData('POST', '/v3/department/getList', {})
    if (response?.data) {
      setDepartments(practionerDepartmentOptions(response.data))
    }
    setDepartmentsLoading(false)
  }

  const fetchPractitioners = async () => {
    setPractitionersLoading(true)
    const response = await fetchData('POST', '/v3/practitioner/getLimitedData', {})
    if (response?.data) {
      setPractitioners(
        response.data.map(item => ({
          label: item?.name,
          value: item?.practitioner_id,
          department_id: normalizeId(item?.department),
        }))
      )
    }
    setPractitionersLoading(false)
  }

  const practitionerOptions = useMemo(() => {
    if (!filters.department_ids?.length) return practitioners
    return practitioners.filter(item =>
      filters.department_ids.includes(item.department_id)
    )
  }, [filters.department_ids, practitioners])

  const selectedDepartmentNames = useMemo(() => {
    if (!filters.department_ids?.length) return t('All')
    return departments
      .filter(item => filters.department_ids.includes(item.value))
      .map(item => item.label)
      .join(', ')
  }, [departments, filters.department_ids, t])

  const selectedPractitionerNames = useMemo(() => {
    if (!filters.practitioner_ids?.length) return t('All')
    return practitioners
      .filter(item => filters.practitioner_ids.includes(item.value))
      .map(item => item.label)
      .join(', ')
  }, [filters.practitioner_ids, practitioners, t])

  const rows = useMemo(() => data?.data || [], [data])

  const groupedRows = useMemo(() => {
    return rows.reduce((acc, row) => {
      const department = row.department_name || t('Unknown Department')
      if (!acc[department]) acc[department] = []
      acc[department].push(row)
      return acc
    }, {})
  }, [rows, t])

  const fetchReport = async () => {
    await executeFetch('POST', '/v3/reports/doctor-schedule', filters)
  }

  const handleResetFilters = async () => {
    setFilters(defaultFilters)
    await executeFetch('POST', '/v3/reports/doctor-schedule', defaultFilters)
  }

  const handleDepartmentChange = e => {
    const departmentIds = e.value || []
    setFilters({
      ...filters,
      department_ids: departmentIds,
      practitioner_ids: filters.practitioner_ids.filter(id => {
        const practitioner = practitioners.find(item => item.value === id)
        return !departmentIds.length || departmentIds.includes(practitioner?.department_id)
      }),
    })
  }

  const handlePrint = async () => {
    if (printRef.current) {
      await handlePrintData(printRef.current.innerHTML, 'Doctor Schedule Report')
    }
  }

  const handleExcelExport = () => {
    exportDoctorScheduleToExcel(
      rows,
      t,
      buisnessInfo,
      selectedDepartmentNames,
      selectedPractitionerNames,
      filters.status
    )
  }

  return (
    <div className="main-page-container !overflow-y-auto scrollbar">
      <div className="flex items-center justify-between mb-4">
        <PageHeader
          title={t('Doctor Schedule Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <div className="mt-2 w-full h-full">
        <div className="flex items-start justify-between gap-3">
          <div className="grid grid-cols-12 lg:grid-cols-11 gap-x-3 mb-2 flex-1">
            <MultiSelectField
              useFormik={false}
              parentClass="col-span-12 md:col-span-4 lg:col-span-2"
              options={departments}
              value={filters.department_ids}
              handleChange={handleDepartmentChange}
              filter
              loading={departmentsLoading}
              placeholder={t('SelectDepartment')}
              selectedItemsLabel={t('{0} departments selected')}
            />
            <MultiSelectField
              useFormik={false}
              parentClass="col-span-12 md:col-span-4 lg:col-span-2"
              options={practitionerOptions}
              value={filters.practitioner_ids}
              handleChange={e =>
                setFilters({ ...filters, practitioner_ids: e.value || [] })
              }
              filter
              loading={practitionersLoading}
              placeholder={t('SelectPractitioner')}
              selectedItemsLabel={t('{0} doctors selected')}
            />
            <MultiSelectField
              useFormik={false}
              parentClass="col-span-12 md:col-span-4 lg:col-span-2"
              options={daysOptions}
              value={filters.days}
              handleChange={e => setFilters({ ...filters, days: e.value || [] })}
              placeholder={t('SelectDay')}
              selectedItemsLabel={t('{0} days selected')}
            />
            <DropdownField
              useFormik={false}
              className="col-span-12 md:col-span-4 lg:col-span-2"
              options={statusOptions.map(item => ({ ...item, label: t(item.label) }))}
              value={filters.status}
              handleChange={e => setFilters({ ...filters, status: e.value })}
              placeholder={t('SelectStatus')}
            />
            <div className="flex items-center gap-2 mb-3 col-span-12 md:col-span-4 lg:col-span-3">
              <CustomButton
                title={t('ApplyFilters')}
                buttonType="primary"
                icon="pi pi-filter"
                className="w-fit h-[35px]"
                disabled={loading || departmentsLoading || practitionersLoading}
                onClick={fetchReport}
              />
              <CustomButton
                title={t('Reset')}
                buttonType="reset"
                icon="pi pi-sync"
                className="w-fit h-[35px]"
                disabled={loading || departmentsLoading || practitionersLoading}
                onClick={handleResetFilters}
              />
            </div>
          </div>

          {rows.length > 0 && (
            <div className="flex items-center gap-2">
              <CustomButton
                title={t('Export to Excel')}
                icon={FaFileExcel}
                onClick={handleExcelExport}
                buttonType="success"
                className="w-fit h-[35px]"
              />
              <CustomButton
                title={t('Print')}
                icon={FaPrint}
                onClick={handlePrint}
                buttonType="danger"
                className="w-fit h-[35px]"
              />
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div ref={printRef} className="bg-white p-6">
          <div className="mb-5">
            <div className="relative flex items-center justify-center min-h-[80px]">
              <div className="absolute left-0 flex items-center">
                {buisnessInfo?.logo ? (
                  <img
                    src={`data:image/png;base64,${buisnessInfo.logo}`}
                    alt="Logo"
                    className="h-24 w-auto object-contain"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-gray-600">
                      {buisnessInfo?.name_en?.charAt(0) || t('H')}
                    </span>
                  </div>
                )}
              </div>
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                  {buisnessInfo?.name_en || t('Hospital Name')}
                </h1>
                <p className="text-lg font-bold text-gray-700 mt-1">
                  {buisnessInfo?.name_ar || t('اسم المستشفى')}
                </p>
                <h2 className="text-base font-bold text-gray-800 mt-3">
                  {t('Doctor Schedule Report')}
                </h2>
              </div>
              <div className="absolute right-0 text-right text-xs text-gray-500">
                <p>{moment().format('DD/MM/YYYY')}</p>
                <p>{moment().format('HH:mm')}</p>
              </div>
            </div>
          </div>

          {Object.entries(groupedRows).map(([department, departmentRows]) => (
            <div key={department} className="mb-6">
              <h3 className="text-sm font-bold text-gray-800 mb-2 uppercase">
                {department}
              </h3>
              <table className="w-full border-collapse border border-gray-400 text-xs">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-400 px-2 py-1.5 text-left">{t('Doctor')}</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-left">{t('Speciality')}</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-left">{t('Room No')}</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-left">{t('Day')}</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-left">{t('Shift')}</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-left">{t('Start Time')}</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-left">{t('End Time')}</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-left">{t('Interval')}</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-left">{t('Limit')}</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-left">{t('Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {departmentRows.map((row, index) => (
                    <tr key={`${row.practitioner_id}-${row.day}-${row.shift_title}-${index}`}>
                      <td className="border border-gray-400 px-2 py-1 font-semibold">{row.doctor_name || '-'}</td>
                      <td className="border border-gray-400 px-2 py-1">{row.speciality || '-'}</td>
                      <td className="border border-gray-400 px-2 py-1">{row.room_no || '-'}</td>
                      <td className="border border-gray-400 px-2 py-1">{row.day || '-'}</td>
                      <td className="border border-gray-400 px-2 py-1">{row.shift_title || '-'}</td>
                      <td className="border border-gray-400 px-2 py-1">{row.start_time || '-'}</td>
                      <td className="border border-gray-400 px-2 py-1">{row.end_time || '-'}</td>
                      <td className="border border-gray-400 px-2 py-1">{row.interval || '-'}</td>
                      <td className="border border-gray-400 px-2 py-1">{row.limit || '-'}</td>
                      <td className="border border-gray-400 px-2 py-1">{row.schedule_status || row.shift_status || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          </div>
        )}

        {!loading && data && rows.length === 0 && (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center">
            <p className="text-gray-500">{t('No schedule data found.')}</p>
          </div>
        )}

        {!loading && !data && (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center">
            <p className="text-gray-500">
              {t('No data available. Please generate a report.')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default DoctorScheduleReport
